import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import swal from "sweetalert";
import { printInfoMessage, routeConstants } from "../../../../shared/constants";
import { selectedFiltersAtom, selectedPlaceAtom, selectedSpatialLayerAtom, smallBusinessMapChangeModeAtom, smallBusinessMapHighlightAtom, smallBusinessMapZoomInAtom, yearsRangeAtom } from "../../../../shared/state/atoms";
import FirmSizeRangeSlider from "../../../../shared/components/FirmSizeRangeSlider";

// Filter for Small Business / Change dashboard.
// Mirrors Intensity filter but replaces single year with start/end year selectors
// and adds Absolute Change / Percentage Change toggle.
function SmallBusinessMapChangeFilter() {
  const [yearsRange] = useAtom(yearsRangeAtom);
  const [selectedFilters, setSelectedFilters] = useAtom(selectedFiltersAtom);
  const [selectedSpatialLayer] = useAtom(selectedSpatialLayerAtom);
  const [selectedPlace] = useAtom(selectedPlaceAtom);
  const [changeMode, setChangeMode] = useAtom(smallBusinessMapChangeModeAtom);
  const [zoomIn, setZoomIn] = useAtom(smallBusinessMapZoomInAtom);
  const [highlightOn, setHighlightOn] = useAtom(smallBusinessMapHighlightAtom);
  const navigate = useNavigate();

  const isHexMode = selectedSpatialLayer?.id === "municipality" && selectedPlace?.category === "A";
  const isProvince = selectedSpatialLayer?.id === "province";
  const showZoomToggle = selectedSpatialLayer && selectedPlace && !isHexMode && !isProvince;

  useEffect(() => {
    if (!selectedFilters.startYear || !selectedFilters.endYear) {
      setSelectedFilters({
        ...selectedFilters,
        startYear: yearsRange[0],
        endYear: yearsRange[yearsRange.length - 1],
      });
    }
  }, [selectedFilters, setSelectedFilters, yearsRange]);

  const handlePrintNavigation = async () => {
    await swal("Please Note", printInfoMessage, "info");
    window.open(`${routeConstants.PDF_PREVIEW}/smallBusinessMapChangePdf`, "_blank");
  };

  const handlePrintAllNavigation = async () => {
    await swal("Please Note", printInfoMessage, "info");
    navigate(`${routeConstants.PDF_PREVIEW}/all`);
  };

  return (
    <div className="w-full">
      <div>
        <div style={{ fontSize: "16px" }} className="font-bold pb-5">
          Toggles:
        </div>
        {/* Change mode toggle — Absolute vs Percentage */}
        <div style={{ borderBottom: "1px solid lightgrey" }} className="mb-4 pb-4">
          <label className="block mb-2">Change Type:</label>
          <div className="flex gap-2">
            <button
              onClick={() => setChangeMode("Absolute Change")}
              className="flex-1 font-sans"
              style={{
                border: changeMode === "Absolute Change" ? "2px solid #84746c" : "2px solid #84746c",
                borderRadius: "50px",
                padding: "6px 10px",
                backgroundColor: changeMode === "Absolute Change" ? "#84746c" : "#fff",
                color: changeMode === "Absolute Change" ? "#fff" : "#84746c",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              Absolute
            </button>
            <button
              onClick={() => setChangeMode("Percentage Change")}
              className="flex-1 font-sans"
              style={{
                border: changeMode === "Percentage Change" ? "2px solid #84746c" : "2px solid #84746c",
                borderRadius: "50px",
                padding: "6px 10px",
                backgroundColor: changeMode === "Percentage Change" ? "#84746c" : "#fff",
                color: changeMode === "Percentage Change" ? "#fff" : "#84746c",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              Percentage
            </button>
          </div>
        </div>
        {/* Start Year */}
        <div className="flex justify-between mb-4">
          <label>Start Year:</label>
          <select
            className="border"
            value={selectedFilters?.startYear ?? ""}
            onChange={(e) =>
              setSelectedFilters({
                ...selectedFilters,
                startYear: e.target.value,
              })
            }
          >
            {yearsRange
              .filter((year) => !selectedFilters.endYear || year <= selectedFilters.endYear)
              .map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
          </select>
        </div>
        {/* End Year */}
        <div style={{ borderBottom: "1px solid lightgrey" }} className="flex justify-between mb-4 pb-4">
          <label>End Year:</label>
          <select
            className="border"
            value={selectedFilters?.endYear ?? ""}
            onChange={(e) =>
              setSelectedFilters({
                ...selectedFilters,
                endYear: e.target.value,
              })
            }
          >
            {yearsRange
              .filter((year) => !selectedFilters.startYear || year >= selectedFilters.startYear)
              .map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
          </select>
        </div>
        {/* Firm Size Slider */}
        <div style={{ borderBottom: "1px solid lightgrey" }} className="mb-4 pb-4">
          <label>Firm Size Range (FTE Employees):</label>
          <FirmSizeRangeSlider
            dashboardId="smallBusinessMapIntensity"
            numHandles={1}
            segmentLabels={["Small (included)", "Medium-Large (excluded)"]}
            segmentColors={["#424242", "#e0e0e0"]}
            allowFullRange
            invertible
          />
        </div>
        {(showZoomToggle || !isHexMode) && (
          <div style={{ borderBottom: "1px solid lightgrey" }} className="mb-4 pb-4">
            {showZoomToggle && (
              <div className="flex justify-between mb-2">
                <label className="form-check-label inline-block text-gray-800" htmlFor="zoomToggleChange">
                  Zoom to Place:
                </label>
                <div className="form-check form-switch">
                  <input
                    checked={zoomIn}
                    onChange={() => setZoomIn(!zoomIn)}
                    className="form-check-input appearance-none w-9 rounded-full h-5 bg-no-repeat bg-contain bg-gray-300 focus:outline-none cursor-pointer shadow-sm checked:bg-blue-500"
                    type="checkbox"
                    role="switch"
                    id="zoomToggleChange"
                  />
                </div>
              </div>
            )}
            <div className="flex justify-between">
              <label className="form-check-label inline-block text-gray-800" htmlFor="highlightToggleChange">
                Highlight Place:
              </label>
              <div className="form-check form-switch">
                <input
                  checked={highlightOn}
                  onChange={() => setHighlightOn(!highlightOn)}
                  className="form-check-input appearance-none w-9 rounded-full h-5 bg-no-repeat bg-contain bg-gray-300 focus:outline-none cursor-pointer shadow-sm checked:bg-red-500"
                  type="checkbox"
                  role="switch"
                  id="highlightToggleChange"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div style={{ marginTop: "30px", borderBottom: "1px solid lightgrey", flexDirection: "column" }} className="flex justify-between mb-4 pb-8">
        <div style={{ fontSize: "16px" }} className="font-bold pb-5">
          Actions:
        </div>
        <div style={{ flexDirection: "row" }}>
          <button
            style={{
              border: "2px solid #F0A400",
              borderRadius: "50px",
              padding: "8px",
              paddingLeft: "14px",
              color: "#F0A400",
              width: "100%",
              marginBottom: "10px",
            }}
            className="font-sans"
            onClick={handlePrintNavigation}
          >
            Print Current Dashboard
          </button>
          <button
            style={{
              border: "2px solid #F0A400",
              borderRadius: "50px",
              padding: "8px",
              paddingLeft: "14px",
              color: "#F0A400",
              width: "100%",
            }}
            className="font-sans"
            onClick={handlePrintAllNavigation}
          >
            Print All Dashboards
          </button>
        </div>
      </div>
    </div>
  );
}

export default SmallBusinessMapChangeFilter;
